import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiAlertCircle, FiCheckCircle, FiClock, FiChevronRight, FiX } from 'react-icons/fi';
import { timeEntryModificationsAPI, monthValidationsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { USER_ROLES } from '../../utils/constants';

const TimeEntryAlerts = () => {
  const { user } = useAuth();
  const [pendingModifications, setPendingModifications] = useState(0);
  const [pendingValidations, setPendingValidations] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  // Solo mostrar para administradores
  const isAdmin = user?.role === USER_ROLES.ESTABLISHMENT_ADMIN ||
                  user?.role === USER_ROLES.PLATFORM_ADMIN;

  useEffect(() => {
    if (isAdmin) {
      loadAlerts();
    }
  }, [isAdmin]);

  const loadAlerts = async () => {
    try {
      setIsLoading(true);

      // Cargar conteo de solicitudes de modificacion pendientes
      const modificationCount = await timeEntryModificationsAPI.getPendingCount();
      setPendingModifications(modificationCount.count || 0);

      // Cargar validaciones pendientes de los ultimos 12 meses
      const now = new Date();
      const currentMonth = now.getMonth() + 1;
      const currentYear = now.getFullYear();

      let totalPending = 0;

      // Revisar los últimos 12 meses (excluyendo el mes actual)
      for (let i = 1; i <= 12; i++) {
        let month = currentMonth - i;
        let year = currentYear;

        if (month <= 0) {
          month += 12;
          year -= 1;
        }

        try {
          const status = await monthValidationsAPI.getStatus(year, month);
          // Solo contar empleados que tienen fichajes
          const pendingWithEntries = status.employees.filter(e => !e.is_validated && e.total_entries > 0);
          totalPending += pendingWithEntries.length;
        } catch {
          // Ignorar errores individuales
        }
      }

      setPendingValidations(totalPending);
    } catch (error) {
      console.error('Error loading time entry alerts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // No mostrar si no es admin, si esta cargando, si fue descartado, o si no hay alertas
  if (!isAdmin || isLoading || dismissed) {
    return null;
  }

  const hasAlerts = pendingModifications > 0 || pendingValidations > 0;

  if (!hasAlerts) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg shadow-sm p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <FiAlertCircle className="h-5 w-5 text-orange-500" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-orange-800 mb-2">
              Tareas Pendientes de Jornada
            </h3>
            <div className="space-y-2">
              {pendingModifications > 0 && (
                <Link
                  to="/time-entries"
                  className="flex items-center justify-between p-2 bg-white rounded-lg border border-orange-200 hover:border-orange-400 hover:shadow-sm transition-all group"
                >
                  <div className="flex items-center gap-2">
                    <FiClock className="h-4 w-4 text-orange-500" />
                    <span className="text-sm text-gray-700">
                      <span className="font-semibold text-orange-600">{pendingModifications}</span>
                      {' '}solicitud{pendingModifications !== 1 ? 'es' : ''} de modificacion pendiente{pendingModifications !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <FiChevronRight className="h-4 w-4 text-gray-400 group-hover:text-orange-500 transition-colors" />
                </Link>
              )}

              {pendingValidations > 0 && (
                <Link
                  to="/time-entries"
                  className="flex items-center justify-between p-2 bg-white rounded-lg border border-orange-200 hover:border-orange-400 hover:shadow-sm transition-all group"
                >
                  <div className="flex items-center gap-2">
                    <FiCheckCircle className="h-4 w-4 text-orange-500" />
                    <span className="text-sm text-gray-700">
                      <span className="font-semibold text-orange-600">{pendingValidations}</span>
                      {' '}validacion{pendingValidations !== 1 ? 'es' : ''} de fichajes pendiente{pendingValidations !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <FiChevronRight className="h-4 w-4 text-gray-400 group-hover:text-orange-500 transition-colors" />
                </Link>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 p-1 text-orange-400 hover:text-orange-600 hover:bg-orange-100 rounded transition-colors"
          title="Descartar"
        >
          <FiX className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default TimeEntryAlerts;
